import { Injectable, Inject } from '@angular/core'
import { HttpClient, HttpResponse } from '@angular/common/http'
import { Observable, switchMap } from 'rxjs'
import { CrudService } from '../../services/crud.service'
import { SDKConfiguration, SDK_CONFIGURATION } from '../../models/config.model'
import { SDK_SETTINGS } from '../../consts/config.const'
import {
  Order,
  OrderAdditionalPackage,
  OrderBulkUpdate,
  OrderBulkUpdateResponse,
  OrderCoverage,
  OrderDashboard,
  OrderExportAmazonShipData,
  OrderPackage,
  Shipment,
} from './models/order.entity'
import { Page } from '../../models/util.model'
import { Product } from '../products'
import { Printers } from '../print-node'
import { OrderSearchParams } from './models/order-search.model'
import { OrderChangeStatusAction } from './models/order.types'
import { OrderCounters } from './models/order-listing.model'

const MODEL = 'orders'
const VERSION = 'v3'
const OLD_VERSION = 'v2'

@Injectable({
  providedIn: 'root',
})
export class OrdersService extends CrudService {
  constructor(
    @Inject(SDK_CONFIGURATION) config: SDKConfiguration,
    http: HttpClient,
  ) {
    super(
      config,
      http,
      `${config.apiUrl}/${SDK_SETTINGS.apiPath}/${VERSION}/${MODEL}`,
      `${config.apiUrl}/${SDK_SETTINGS.apiPath}/${OLD_VERSION}/${MODEL}`,
    )
  }

  // ============================================================================
  // CRUD Operations
  // ============================================================================

  /**
   * Read a order by ID
   * @param orderId - The order ID
   * @returns The observable<Order> for read the order
   */
  read$(orderId: string): Observable<Order> {
    return this._read$<Order>(orderId)
  }

  /**
   * Update a order by ID
   * @param orderId - The order ID
   * @param order - The order body to update
   * @returns The observable<Order> for update the order
   */
  update$(orderId: string, order: Order): Observable<Order> {
    return this._update$<Order>(orderId, order)
  }

  /**
   * Create or update a order by ID
   * @param orderId - The order ID
   * @param order - The order body to update
   * @returns The observable<Order> for update the order
   */
  upsert$(order: Order): Observable<Order> {
    return this._upsert$<Order>(order, order._id)
  }

  /**
   * Delete a order by ID
   * @param orderId - The order ID
   * @returns The observable<Order> for delete the order
   */
  delete$(orderId: string): Observable<Order> {
    return this._delete$<Order>(orderId)
  }

  /**
   * Search orders by params
   * @param params - The search params
   * @param returnAll - the returnAll flag
   * @returns The observable<Page<Order>> for search orders
   */
  search$(
    params?: OrderSearchParams,
    returnAll = false,
  ): Observable<Page<Order>> {
    return this._search$<Order>(params, returnAll)
  }

  /**
   * List orders by params
   * @param params - The search params
   * @param returnAll - the returnAll flag
   * @returns The observable<Order[]> for list orders
   */
  list$(params?: OrderSearchParams, returnAll = false): Observable<Order[]> {
    return this._list$<Order>(params, returnAll)
  }

  // ============================================================================
  // Status Management
  // ============================================================================

  /**
   * Cancel an order by ID
   * @param orderId - The order ID
   * @returns The observable<Order> for cancel the order
   */
  cancel$(orderId: string): Observable<Order> {
    return this.http.post<Order>(this.buildUrl(orderId, 'cancel'), {})
  }

  /**
   * Cancel an order shipment by ID
   * @param orderId - The order ID
   * @returns The observable<Order> for cancel the shipment
   */
  cancelShipment$(orderId: string): Observable<void> {
    return this.http.post<void>(
      this.buildUrl(orderId, 'shipment', 'cancel'),
      {},
    )
  }

  /**
   * Confirm an order by ID
   * @param orderId - The order ID
   * @returns The observable<Order> for confirm the order
   */
  confirm$(orderId: string): Observable<Order> {
    return this.http.post<Order>(this.buildUrl(orderId, 'confirm'), {})
  }

  /**
   * Deliver an order by ID
   * @param orderId - The order ID
   * @returns The observable<Order> for deliver the order
   */
  deliver$(orderId: string): Observable<Order> {
    return this.http.post<Order>(this.buildUrl(orderId, 'deliver'), {})
  }

  /**
   * DropShip an order by ID
   * @param orderId - The order ID
   * @returns The observable<Order> for dropship the order
   */
  dropship$(orderId: string): Observable<Order> {
    return this.http.post<Order>(this.buildUrl(orderId, 'dropship'), {})
  }

  /**
   * Hand an order by ID
   * @param orderId - The order ID
   * @returns The observable<Order> for hand the order
   */
  hand$(orderId: string): Observable<Order> {
    return this.http.post<Order>(this.buildUrl(orderId, 'hand'), {})
  }

  /**
   * Manage an order by ID (status PROCESSING)
   * @param orderId - The order ID
   * @returns The observable<Order> for manage the order
   */
  manage$(orderId: string): Observable<Order> {
    return this.http.post<Order>(this.buildUrl(orderId, 'manage'), {})
  }

  /**
   * Pack an order by ID
   * @param orderId - The order ID
   * @param printers - The printers IDs where print the label
   * @returns The observable<Order> for pack the order
   */
  pack$(
    orderId: string,
    params: {
      printers?: Printers
      packages?: OrderPackage[]
      additionalPackages?: OrderAdditionalPackage[]
    } = {},
  ): Observable<Order> {
    return this.http.post<Order>(this.buildUrl(orderId, 'pack'), params)
  }

  /**
   * Park an order by ID
   * @param orderId - The order ID
   * @returns The observable<Order> for park the order
   */
  park$(orderId: string): Observable<Order> {
    return this.http.post<Order>(this.buildUrl(orderId, 'park'), {})
  }

  /**
   * Pend an order by ID
   * @param orderId - The order ID
   * @returns The observable<Order> for pend the order
   */
  pend$(orderId: string): Observable<Order> {
    return this.http.post<Order>(this.buildUrl(orderId, 'pend'), {})
  }

  /**
   * Process an order by ID
   * @param orderId - The order ID
   * @returns The observable<Order> for process the order
   */
  process$(orderId: string): Observable<Order> {
    return this.http.post<Order>(this.buildUrl(orderId, 'process'), {})
  }

  /**
   * Ship an order by ID
   * @param orderId - The order ID
   * @returns The observable<Order> for ship the order
   */
  ship$(orderId: string): Observable<Order> {
    return this.http.post<Order>(this.buildUrl(orderId, 'ship'), {})
  }

  /**
   * Change order status
   * @param orderId - The order ID
   * @param action - The change status action
   * @param actionOptions - The action options
   * @returns The Observable<Order> for change the order status
   */
  changeStatus$(
    orderId: string,
    action: OrderChangeStatusAction,
    params?: {
      printers?: Printers
      packages?: OrderPackage[]
      additionalPackages?: OrderAdditionalPackage[]
    },
  ): Observable<Order> {
    switch (action) {
      case 'cancel':
        return this.cancel$(orderId)
      case 'cancelShipment':
        return this.cancelShipment$(orderId).pipe(
          switchMap(() => this.read$(orderId)),
        )
      case 'confirm':
        return this.confirm$(orderId)
      case 'deliver':
        return this.deliver$(orderId)
      case 'dropship':
        return this.dropship$(orderId)
      case 'hand':
        return this.hand$(orderId)
      case 'manage':
        return this.manage$(orderId)
      case 'pack':
        return this.pack$(orderId, params)
      case 'park':
        return this.park$(orderId)
      case 'pend':
        return this.pend$(orderId)
      case 'process':
        return this.process$(orderId)
      case 'ship':
        return this.ship$(orderId)
    }
  }

  // ============================================================================
  // Attachments
  // ============================================================================

  /**
   * Download order label by ID and labelID
   * @param orderId - The order ID
   * @param labelId - The label ID
   * @returns The observable<any> for download the order label
   */
  downloadLabel$(
    orderId: string,
    labelId: string,
  ): Observable<HttpResponse<Blob>> {
    return this.http.get(
      this.buildUrl(orderId, 'files', 'shipment', 'labels', labelId),
      {
        responseType: 'blob',
        observe: 'response',
      },
    )
  }

  /**
   * Converts a ZPL order label into a PDF file
   * @param zpl - The label in ZPL format
   * @returns The observable<any> for download the order label
   */
  downloadPdfLabel$(zpl: string): Observable<Blob> {
    return this.http.post(
      `https://api.labelary.com/v1/printers/8dpmm/labels/4x6/0`,
      zpl,
      {
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
          Accept: 'application/pdf',
        },
        responseType: 'blob',
      },
    )
  }

  /**
   * Print order labels
   * @param orderId - The order ID
   * @param printerId - The printer ID
   * @returns The observable<void> for print the order labels
   */
  printLabel$(orderId: string, printers: Printers): Observable<void> {
    return this.http.post<void>(this.buildUrl(orderId, 'print'), {
      printers,
    })
  }

  /**
   * Download the order summary
   * @param orderId - The order ID
   * @returns The observable<any> for download the order summary
   */
  downloadSummary$(orderId: string): Observable<HttpResponse<Blob>> {
    return this.http.get(this.buildUrl(orderId, 'files', 'summary'), {
      responseType: 'blob',
      observe: 'response',
    })
  }

  /**
   * Print order summary
   * @param orderId - The order ID
   * @param printerId - The printer ID
   * @returns The observable<void> for print the order summary
   */
  printSummary$(orderId: string, printerId: string): Observable<void> {
    return this.http.post<void>(
      this.buildUrl(orderId, 'files', 'summary', 'print'),
      { printerId },
    )
  }

  downloadDdt$(
    orderId: string,
    shipmentId: string,
  ): Observable<HttpResponse<Blob>> {
    return this.http.get(
      this.buildUrl(orderId, 'shipments', shipmentId, 'files', 'ddt'),
      {
        responseType: 'blob',
        observe: 'response',
      },
    )
  }

  printDdt$(
    orderId: string,
    shipmentId: string,
    printerId: string,
  ): Observable<void> {
    return this.http.post<void>(
      this.buildUrl(orderId, 'shipments', shipmentId, 'files', 'ddt', 'print'),
      { printerId },
    )
  }

  /**
   * Get old orders upload url
   * @deprecated
   * @returns the url to upload orders by file
   */
  getOldUploadOrdersUrl(): string {
    return this.buildDeprecatedUrl('import-excel/')
  }

  /**
   * Get orders upload url
   * @returns the url to upload orders by file
   */
  getUploadOrdersUrl(): string {
    return this.buildUrl('import-excel/')
  }

  /**
   * Get orders upload url
   * @returns the url to upload Amazon orders by file
   */
  getUploadAmazonOrdersUrl(): string {
    return this.buildUrl('import-amazon-txt')
  }

  /**
   * Get order rows upload url
   * @deprecated
   * @param orderId - The order ID
   * @returns the url to upload the order rows by file
   */
  getUploadOrderRowsUrl(orderId: string): string {
    return this.buildDeprecatedUrl(orderId, 'import-rows/')
  }

  /**
   * Download template for upload orders
   * @deprecated
   * @returns the observable<Blob> for download the template
   */
  downloadOldOrdersUploadTemplate$(): Observable<HttpResponse<Blob>> {
    return this.http.get(this.buildUrl('template'), {
      responseType: 'blob',
      observe: 'response',
    })
  }

  /**
   * Download template for upload orders
   * @returns the observable<Blob> for download the template
   */
  downloadOrdersUploadTemplate$(): Observable<HttpResponse<Blob>> {
    return this.http.get(this.buildUrl('template'), {
      responseType: 'blob',
      observe: 'response',
    })
  }

  /**
   * Download template for upload order rows
   * @deprecated
   * @returns the observable<Blob> for download the template
   */
  downloadOrderRowsUploadTemplate$(): Observable<HttpResponse<Blob>> {
    return this.http.get(this.buildDeprecatedUrl('single-template'), {
      responseType: 'blob',
      observe: 'response',
    })
  }

  /**
   * Generates the path to which attachments for an order must be uploaded
   * @param orderId - The order ID
   * @returns the path
   */
  getOrderAttachmentsUploadUrl(orderId: string): string {
    return this.buildUrl(orderId, 'attachments')
  }

  /**
   * Deletes an attachment from an order
   * @param orderId - The order ID
   * @param attachmentId - The attachment ID
   */
  deleteAttachment$(orderId: string, attachmentId: string): Observable<Order> {
    return this.http.delete<Order>(
      this.buildUrl(orderId, 'attachments', attachmentId),
      { body: {} },
    )
  }

  /**
   * Downloads an attachment from an order
   * @param productId - The product ID
   * @param attachmentId - The attachment ID
   * @returns the observable<Attachment>
   */
  downloadAttachment$(
    orderId: string,
    attachmentId: string,
  ): Observable<HttpResponse<Blob>> {
    return this.http.get(
      this.buildUrl(orderId, 'attachments', attachmentId, 'content'),
      { responseType: 'blob', observe: 'response' },
    )
  }

  /**
   * Download customs invoice
   * @param orderId - The id of the order
   * @param shipmentId - The id of the shipment
   * @returns the observable<Attachment>
   */
  downloadInvoice$(
    orderId: string,
    shipmentId: string,
  ): Observable<HttpResponse<Blob>> {
    return this.http.get(
      this.buildUrl(orderId, 'shipments', shipmentId, 'files', 'customs'),
      { responseType: 'blob', observe: 'response' },
    )
  }

  // ============================================================================
  // Shipments Management
  // ============================================================================

  /**
   * Get order shipment
   * @param orderId - The order ID
   * @returns The observable<Order> for ship the order
   */
  getShipment$(orderId: string): Observable<Shipment> {
    return this.http.get<Shipment>(this.buildUrl(orderId, 'shipment'))
  }

  /**
   * Export Amazon shippings
   * @param exportData - The export params
   * @returns the observable<void> for export
   */
  exportAmazonShip$(exportData: OrderExportAmazonShipData): Observable<void> {
    return this.http.post<void>(
      this.buildUrl('amazon-export').replace('orders', 'shipments'),
      exportData,
    )
  }

  /**
   * Requests the shipment for an order
   * @param orderId - The id of the order
   * @returns the observable<Attachment>
   */
  requestShipment$(orderId: string): Observable<void> {
    return this.http.post<void>(this.buildUrl(orderId, 'request-shipment'), {})
  }

  // ============================================================================
  // Operations
  // ============================================================================

  /**
   * Park an order package by ID
   * @param orderId - The order ID
   * @returns The observable<Order> for park the package
   */
  parkPackage$(orderId: string, packageId: string): Observable<Order> {
    return this.http.post<Order>(
      this.buildUrl(orderId, 'packages', packageId, 'park'),
      {},
    )
  }

  /**
   * List order products
   * @param orderId - The order ID
   * @returns The observable<Order> for process the order
   */
  getProducts$(
    orderId: string,
    params?: { limit?: number; offset?: number },
  ): Observable<Product[]> {
    return this.http.get<Product[]>(this.buildUrl(orderId, 'products'), {
      params,
    })
  }

  /**
   * Duplicate order by ID
   * @param orderId - The order ID
   * @returns The observable<Order> for duplicate the order
   */
  duplicate$(orderId: string): Observable<Order> {
    return this.http.post<Order>(this.buildUrl(orderId, 'duplicate'), {})
  }

  /**
   * Split order
   * @param orderId - The order ID
   * @param currentOrder - The new shape of the order
   * @param newOrders - New orders to create
   * @returns The observable<Order[]> for split the order
   */
  split$(orderId: string, children: Order[]): Observable<Order[]> {
    return this.http.post<Order[]>(this.buildUrl(orderId, 'split'), {
      children,
    })
  }

  /**
   * Pick a product from the order
   * @param orderId - The order ID
   * @param rowId - The order row ID
   * @param params - The pick params
   * @returns The observable<Order> for pick the order product
   */
  pick$(
    orderId: string,
    rowId: string,
    params: {
      productId: string
      quantity: number
      locationId: string
      lot?: string
      expirationDate?: string
      serial?: string
    },
  ): Observable<Order> {
    return this.http.post<Order>(
      this.buildUrl(orderId, 'rows', rowId, 'pick'),
      params,
    )
  }

  /**
   * Reset the order picking: change the status from PROCESSING to CONFIRMED
   * @param orderId - The order ID
   * @returns The observable<Order> for reset the picking
   */
  resetPicking$(orderId: string): Observable<Order> {
    return this.http.post<Order>(this.buildUrl(orderId, 'picking', 'reset'), {})
  }

  /**
   * Get order coverage by ID
   * @param orderId - The order ID
   * @returns The Observable<OrderCoverage> for get order coverage
   */
  coverage$(orderId: string): Observable<OrderCoverage> {
    return this.http.get<OrderCoverage>(this.buildUrl(orderId, 'coverage'))
  }

  /**
   * Get orders coverage by warehouse
   * @param warehouseId - The warehouse ID
   * @param orderIds - The order IDs
   * @returns The Observable<OrderCoverage[]> for get orders coverage
   */
  multiCoverage$(
    warehouseId: string,
    orderIds: string[],
  ): Observable<OrderCoverage[]> {
    return this.http.post<OrderCoverage[]>(this.buildUrl('coverage'), {
      warehouseId,
      orderIds,
    })
  }

  /**
   * Bulk update orders
   * @param params - The bulk update params
   * @returns The observable<OrderBulkUpdateResponse> for update orders
   */
  bulkUpdate$(params: OrderBulkUpdate): Observable<OrderBulkUpdateResponse> {
    return this.http.post<OrderBulkUpdateResponse>(
      this.buildUrl('bulk-update'),
      params,
    )
  }

  // ============================================================================
  // Counters & Statistics
  // ============================================================================

  /**
   * Get dashboard order results
   * @param startDate - The start date
   * @param endDate - The end date
   * @returns The observable<OrderDashboard> for dashboard search
   */
  dashboard$(startDate: string, endDate: string): Observable<OrderDashboard> {
    return this.http.get<OrderDashboard>(this.buildUrl('stats', 'dashboard'), {
      params: {
        'header.rifDate:ge': startDate,
        'header.rifDate:lt': endDate,
      },
    })
  }

  /**
   * Get order counters
   * @returns The observable<OrderCounters> for order count
   */
  counts$(): Observable<OrderCounters> {
    return this.http.get<OrderCounters>(this.buildUrl('stats', 'status'))
  }
}
